import { Field, reduxForm, stopSubmit } from 'redux-form'
import * as yup from 'yup'
import { Component, ReactNode } from 'react'
import * as reduce from 'lodash/reduce'
import * as pick from 'lodash/pick'
import * as isNumber from 'lodash/isNumber'
import * as styles from 'styles/molecules/forms/LinkageForm.css'
import { OnSubmitReduxForm, ReduxFormProps, StoreDispatch } from 'interfaces/component'
import { AnyDict } from 'interfaces'
import { TYPE_REQUIRED } from 'constants/errors'
import { getSaveButtonFormProps, shouldAsyncValidate } from 'helpers/formHelpers'
import { getProviderLogoPath } from 'helpers/linkageHelpers'
import Icon from 'components/atoms/Icon'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import RadioButtonList from 'components/atoms/inputs/RadioButtonList'
import CaptchaWrapped from 'components/atoms/inputs/CaptchaWrapped'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import Alert from 'components/atoms/Alert'
import Button from 'components/features/pfm/Button/Button'
import { FORM_LINKAGE_AUTHENTICATION } from 'constants/forms'
import { MapDispatchToPropsParam } from 'react-redux'
import { LinkageFormField, LinkageFormFieldOption, LinkageFormFieldText, LinkageSessionId } from 'interfaces/linkage'
import { createSelector } from 'reselect'
import { linkageFormBySessionSelector, linkageFormTimeoutBySessionSelector, providerBySessionSelector } from 'selectors/linkageSelectors'
import { asyncValidator } from 'helpers/schemaHelpers'
import { connectOptionally, OptionalRendererProps } from 'tangle-shared'
import { captureWarning } from 'helpers/errorHelpers'
import { createOptionalRenderer } from 'tangle-shared/lib'

function isTextField (field: LinkageFormField): field is LinkageFormFieldText {
  return ['text', 'password'].indexOf(field.type) !== -1
}

type LinkageFormStateProps = {
  logo: string
  name: string
  instructions?: string
  forget_password_url?: string
  timeout: number | null
}

type LinkageFormDispatchProps = {
  resetLinkageForm: () => void
}

type LinkageFormOwnProps =
  & Pick<ReduxFormProps, 'initialValues' | 'onSubmit'>
  & {
    sessionId: LinkageSessionId
    fields: LinkageFormField[]
  }

type LinkageFormProps =
  & ReduxFormProps
  & LinkageFormStateProps
  & LinkageFormDispatchProps
  & LinkageFormOwnProps

type LinkageFormState = {
  timeout: number | null
}

const asyncValidate = (values: FormData, _dispatch: StoreDispatch, props: LinkageFormProps): Promise<any> => {
  const { fields } = props
  const schema = reduce(fields, (result: AnyDict, field: LinkageFormField): AnyDict => {
    if (isTextField(field)) {
      let fieldSchema = yup.string()
      if (!field.is_optional) {
        fieldSchema = fieldSchema.required(TYPE_REQUIRED)
      }
      result[field.name] = fieldSchema
    }
    return result
  }, {})

  return asyncValidator(yup.object().shape(schema))(values)
}

@reduxForm({
  form: FORM_LINKAGE_AUTHENTICATION,
  shouldAsyncValidate,
  asyncValidate: asyncValidate as any,
})
class LinkageFormComponent extends Component<LinkageFormProps, LinkageFormState> {

  private timeoutId: number | null = null

  constructor (props: LinkageFormProps, context: any) {
    super(props, context)

    this.state = this.calculateState(props)
  }

  render () {
    const {
      name,
      logo,
      instructions,
      forget_password_url,
      handleSubmit,
      error,
      resetLinkageForm,
    } = this.props

    const formError = error ? (
      <div className={ styles.errorWrapper }>
        <Alert onDismiss={ resetLinkageForm } theme='danger'>
          { error }
        </Alert>
      </div>
    ) : null

    const logoPath = getProviderLogoPath(name)

    return (
      <form
        className={ styles.accountLinkageForm }
        onSubmit={ handleSubmit ? handleSubmit(this.onSubmit) : undefined }
        autoComplete='off'
      >
        {
          ['authenticationId', 'stageName', 'sessionId'].map((hiddenFieldName) => (
            <Field key={ hiddenFieldName } name={ hiddenFieldName } props={{ type: 'hidden' }} component='input' />
          ))
        }
        <section className={ styles.fieldsContainer }>
          <div className={ styles.logo } style={{ backgroundImage: `url(${ logoPath || logo })` }} />
          <h4 className={ styles.name }>
            { name }
          </h4>
          { Boolean(instructions) && (
            <div className={ styles.instructions }>
              { instructions }
            </div>
          )}
          { this.generateFields() }
          { formError }
          { this.renderTimeoutChild() }
          <div className={ styles.buttonWrapper }>
            <Button
              { ...getSaveButtonFormProps(this.props) }
              submit
              fullWidth
              qaMarker='submit-button'
            >
              Link Your Account
            </Button>
          </div>
          <NavigationLink className={ styles.secureLink } to='security' target='_blank'>
            <span className={ styles.secureIconWrapper }>
              <Icon kind='secure' />
            </span>
            This is a secure environment
          </NavigationLink>
          { forget_password_url && (
            <a
              href={ forget_password_url }
              className={ styles.forgetPassword }
              target='_blank'
              rel='noopener noreferrer'
            >
              Forgot your credentials?
            </a>
          )}
        </section>
      </form>
    )
  }

  renderTimeoutChild = () => {
    const { timeout } = this.state
    if (!isNumber(timeout)) {
      return null
    }

    const timeoutChild = (
      <div className={ styles.timeoutValue }>
        { timeout > 1 ? `${ timeout } seconds` : `a moment` }.
      </div>
    )

    return (
      <div className={ styles.timeoutWrapper }>
        <div className={ styles.timeoutCopy }>
          For your security, this form expires in
        </div>
        { timeoutChild }
      </div>
    )
  }

  componentDidMount () {
    this.initiateCountdown()
  }

  componentWillUnmount () {
    if (isNumber(this.timeoutId)) {
      window.clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  private calculateState = (props: LinkageFormProps): LinkageFormState => {
    const { timeout } = props
    return {
      timeout: isNumber(timeout) ? Math.ceil(timeout / 1000) : null,
    }
  }

  // TODO @rendalf send warning to sentry if we stumbled across unknown field
  private generateField = (field: LinkageFormFieldText | LinkageFormFieldOption, index: number) => {
    const { type, label, name } = field
    const { error, resetLinkageForm } = this.props
    let component: ReactNode = null
    const autoFocus = index === 0
    const fieldKey = `${ this.props.sessionId }-${ name }`
    if (isTextField(field)) {
      const { image } = field
      const autoComplete = 'off'
      const componentClass = image ? CaptchaWrapped : TextFieldWrapped
      const onValueChange = error ? resetLinkageForm : (): null => null
      component = (
        <Field
          key={ fieldKey }
          name={ name }
          component={ componentClass }
          props={{
            label,
            type: type === 'password' ? 'text-secure' : 'text',
            image,
            autoComplete,
            autoFocus,
            onValueChange,
            qaMarker: field.name,
            qaType: type,
            spellCheck: false,
          }}
        />
      )
    } else if (type === 'option' || type === 'radio') {
      const items = field.options.map(({ value, display }) => ({ label: display, value }))
      component = (
        <Field
          key={ fieldKey }
          name={ field.name }
          component={ RadioButtonList }
          props={{
            label,
            items,
            autoFocus,
            name: field.name,
          }}
        />
      )
    }

    return (
      <li
        data-qa-type='linkage-form-field'
        key={ index }
        className={ styles.fieldListItem }
      >
        { component }
      </li>
    )
  }

  private generateFields () {
    const { fields } = this.props
    return (
      <ul className={ styles.fieldList }>
        { fields.map(this.generateField) }
      </ul>
    )
  }

  private initiateCountdown = () => {
    const { timeout } = this.state
    if (!timeout || timeout <= 0) {
      return
    }

    this.timeoutId = window.setTimeout(() => {
      const nextState = {
        timeout: timeout - 1,
      }
      this.setState(nextState, this.initiateCountdown)
    }, 1000)
  }

  private onSubmit = (values: AnyDict, dispatch: StoreDispatch, form: LinkageFormProps): {} => {
    // filter values according to the schema
    const { fields } = this.props
    const onSubmit = this.props.onSubmit as OnSubmitReduxForm
    const filteredValues = pick(values, fields.map((field) => field.name))

    return onSubmit(filteredValues, dispatch, form)
  }
}


const createMapStateToProps = () => {

  const optionalRenderer = createOptionalRenderer<LinkageFormStateProps>()

  return createSelector(
    linkageFormBySessionSelector,
    linkageFormTimeoutBySessionSelector,
    providerBySessionSelector,
    (form, timeout, provider): OptionalRendererProps<LinkageFormStateProps> => {
      if (!form || !provider) {
        return optionalRenderer.fallback()
      }

      if (isNumber(timeout) && timeout < 0) {
        captureWarning(`MFA form timeout has expired`, {
          timeout,
        })

        return optionalRenderer.fallback()
      }

      return optionalRenderer.render({
        logo: provider.logo,
        name: provider.name,
        forget_password_url: form.forget_password_url,
        timeout,
        // TODO
        instructions: '',
      })
    },
  )
}

const mapDispatchToProps: MapDispatchToPropsParam<LinkageFormDispatchProps> = {
  resetLinkageForm: () => stopSubmit(FORM_LINKAGE_AUTHENTICATION),
}

const LinkageForm = connectOptionally<
  LinkageFormStateProps, LinkageFormDispatchProps, LinkageFormOwnProps
>(createMapStateToProps, mapDispatchToProps)(LinkageFormComponent)

export default LinkageForm
